<template>
  <div>
    <b-row class="match-height">
      <b-col
        v-for="card in firstRowStatisticCards"
        :key="card.status"
        cols="12"
        md="6"
      >
        <rc-overlay
          :show="statsLoading"
          rounded
        >
          <statistic-card-horizontal
            :icon="card.icon"
            :statistic="card.value"
            :statistic-title="card.title"
            :color="card.color"
          />
        </rc-overlay>
      </b-col>
    </b-row>

    <b-row class="match-height d-none d-md-flex">
      <b-col
        v-for="card in secondRowStatisticCards"
        :key="card.status"
        cols="12"
        md="4"
      >
        <rc-overlay
          :show="statsLoading"
          rounded
        >
          <statistic-card-horizontal
            :icon="card.icon"
            :statistic="card.value"
            :statistic-title="card.title"
            :color="card.color"
          />
        </rc-overlay>
      </b-col>
    </b-row>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="p-1">
        <b-row class="align-items-end">
          <b-col
            cols="12"
            md="2"
            class="d-flex align-items-end"
          >
            <b-form-group
              :label="$t('partner.order-request.filters.limit')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-select
                v-model="query.limit"
                :options="limitOptions"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="4"
            class="ml-auto"
          >
            <b-form-group
              :label="$t('partner.order-request.filters.status')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-select
                v-model="query.status"
                :options="statusOptions"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
            class="d-flex justify-content-md-end"
          >
            <b-button
              variant="primary"
              :to="{ name: 'partner-order-request-create' }"
            >
              {{ $t('partner.order-request.actions.create') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <rc-overlay :show="loading">
        <b-table
          :items="requests"
          :fields="tableColumns"
          responsive
          primary-key="id"
          show-empty
          :empty-text="loading ? $t('shared.loading') : $t('shared.no-data')"
        >
          <template #cell(status)="data">
            <div class="d-flex flex-column align-items-start">
              <b-badge
                class="font-medium-1"
                :variant="statusVariant(data.item.status)"
              >
                {{ statusLabel(data.item.status) }}
              </b-badge>
              <b-badge
                v-if="priorityLabel(data.item.priority) !== '-'"
                pill
                class="mt-25 font-small-1"
                :variant="priorityVariant(data.item.priority)"
              >
                {{ priorityLabel(data.item.priority) }}
              </b-badge>
            </div>
          </template>

          <template #cell(customerName)="data">
            {{ resolveCustomerName(data.item) }}
          </template>

          <template #cell(requestedType)="data">
            <b-badge
              pill
              variant="light-info"
            >
              {{ requestedTypeLabel(data.item.requestedType) }}
            </b-badge>
          </template>

          <template #cell(requestedStartAt)="data">
            <b-badge
              variant="light-secondary"
              class="text-nowrap"
            >
              {{ formatDate(data.item.requestedStartAt) }}
            </b-badge>
          </template>

          <template #cell(updatedAt)="data">
            <b-badge
              variant="light-secondary"
              class="text-nowrap"
            >
              {{ formatDate(data.item.updatedAt) }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              :to="{ name: 'partner-order-request-details', params: { id: data.item.id } }"
            >
              {{ $t('partner.order-request.actions.open') }}
            </b-button>
          </template>
        </b-table>
      </rc-overlay>

      <table-bottom
        :query="query"
        :total="total"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormSelect,
  BRow,
  BTable,
} from 'bootstrap-vue'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import { transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import TableBottom from '@/layouts/components/table/TableBottom.vue'
import {
  getPriorityLabelKey,
  getPriorityVariant,
  mapApiPriorityToCode,
} from '@/views/external-order-request/helpers/priority'
import PusherClient from '@/libs/pusher'
import { getExternalOrderRequestStatusVariant } from '../review/helpers/reviewFormModel'

export default {
  name: 'PartnerOrderRequestList',
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormSelect,
    BRow,
    BTable,
    StatisticCardHorizontal,
    RcOverlay,
    TableBottom,
  },
  data() {
    return {
      loading: false,
      statsLoading: false,
      customerLookupById: {},
      query: {
        status: null,
        limit: 25,
        page: 1,
        sortField: 'createdAt',
        sortDir: 'DESC',
      },
      limitOptions: [10, 25, 50, 100].map(limit => ({ value: limit, text: limit })),
      refreshTimeoutId: null,
      pusherExternalOrderRequestHandler: null,
    }
  },
  computed: {
    requests() {
      return this.$store.getters['externalOrderRequest/getPartnerOrderRequests']
    },
    total() {
      return this.$store.getters['externalOrderRequest/getPartnerOrderRequestsTotal']
    },
    statusTotals() {
      return this.$store.getters['externalOrderRequest/getPartnerOrderRequestStatusTotals']
    },
    statuses() {
      return this.$store.getters['externalOrderRequest/getExternalOrderRequestStatuses']
    },
    allowedCustomers() {
      return this.$store.getters['externalOrderRequest/getAllowedCustomers']
    },
    statusOptions() {
      return [
        { value: null, text: this.$t('partner.order-request.filters.any-status') },
        ...this.statuses.map(status => ({
          value: status,
          text: this.statusLabel(status),
        })),
      ]
    },
    tableColumns() {
      return [
        { key: 'status', label: this.$t('partner.order-request.fields.status') },
        { key: 'customerName', label: this.$t('partner.order-request.fields.customer') },
        { key: 'requestedType', label: this.$t('partner.order-request.fields.requested-type') },
        { key: 'requestedStartAt', label: this.$t('partner.order-request.fields.requested-start-at') },
        { key: 'updatedAt', label: this.$t('partner.order-request.fields.updated-at') },
        { key: 'actions', label: this.$t('shared.actions') },
      ]
    },
    firstRowStatisticCards() {
      return [
        {
          status: 'draft',
          title: this.statusLabel('draft'),
          value: this.statusTotals.draft || 0,
          icon: 'FileTextIcon',
          color: 'secondary',
        },
        {
          status: 'changes_requested',
          title: this.statusLabel('changes_requested'),
          value: this.statusTotals.changes_requested || 0,
          icon: 'Edit3Icon',
          color: 'warning',
        },
      ]
    },
    secondRowStatisticCards() {
      return [
        {
          status: 'submitted',
          title: this.statusLabel('submitted'),
          value: this.statusTotals.submitted || 0,
          icon: 'SendIcon',
          color: 'warning',
        },
        {
          status: 'accepted',
          title: this.statusLabel('accepted'),
          value: this.statusTotals.accepted || 0,
          icon: 'CheckIcon',
          color: 'success',
        },
        {
          status: 'rejected_or_cancelled',
          title: `${this.statusLabel('rejected')} / ${this.statusLabel('cancelled')}`,
          value: (this.statusTotals.rejected || 0) + (this.statusTotals.cancelled || 0),
          icon: 'XIcon',
          color: 'danger',
        },
      ]
    },
  },
  watch: {
    query: {
      deep: true,
      handler() {
        this.fetchData()
      },
    },
  },
  created() {
    this.initializePage()
  },
  mounted() {
    this.subscribeToPusher()
  },
  beforeDestroy() {
    this.unsubscribeFromPusher()
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId)
      this.refreshTimeoutId = null
    }
  },
  methods: {
    async initializePage() {
      await this.fetchAllowedCustomersLookup()
      this.fetchData()
    },
    async fetchAllowedCustomersLookup() {
      try {
        await this.$store.dispatch('externalOrderRequest/fetchAllowedCustomers')
      } catch (error) {
        this.customerLookupById = {}
        return
      }

      this.customerLookupById = this.allowedCustomers.reduce((acc, item) => {
        if (item?.id) {
          acc[item.id] = item.name
        }

        return acc
      }, {})
    },
    fetchData() {
      this.loading = true
      this.statsLoading = true
      const payload = {
        limit: this.query.limit,
        page: this.query.page,
        sortField: this.query.sortField,
        sortDir: this.query.sortDir,
      }

      if (this.query.status) {
        payload.status = this.query.status
      }

      Promise.all([
        this.$store.dispatch('externalOrderRequest/fetchPartnerOrderRequests', payload),
        this.refreshStatusTotals(),
      ]).catch(() => {
        window.toast.notify.danger(this.$t('partner.order-request.errors.fetch-list'))
      })
        .finally(() => {
          this.loading = false
          this.statsLoading = false
        })
    },
    refreshStatusTotals() {
      return this.$store.dispatch('externalOrderRequest/fetchPartnerOrderRequestStatusTotals')
    },
    scheduleRealtimeRefresh() {
      if (this.refreshTimeoutId) {
        return
      }

      this.refreshTimeoutId = setTimeout(() => {
        this.refreshTimeoutId = null
        this.fetchData()
      }, 350)
    },
    subscribeToPusher() {
      if (this.pusherExternalOrderRequestHandler) {
        return
      }

      this.pusherExternalOrderRequestHandler = () => this.scheduleRealtimeRefresh()
      PusherClient.bind('update', 'external_order_request', this.pusherExternalOrderRequestHandler, this)
    },
    unsubscribeFromPusher() {
      if (!this.pusherExternalOrderRequestHandler) {
        return
      }

      PusherClient.unbind('update', 'external_order_request', this.pusherExternalOrderRequestHandler, this)
      this.pusherExternalOrderRequestHandler = null
    },
    requestedTypeLabel(type) {
      if (!type) {
        return '-'
      }

      const i18nKey = `order.types.${type}`
      if (typeof this.$te === 'function' && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }

      return type
    },
    resolveCustomerName(item) {
      if (item?.customerName) {
        return item.customerName
      }

      if (item?.customerId && this.customerLookupById[item.customerId]) {
        return this.customerLookupById[item.customerId]
      }

      return item?.customerId || '-'
    },
    priorityLabel(priorityValue) {
      const code = mapApiPriorityToCode(priorityValue)
      if (!code) {
        return '-'
      }

      return this.$t(getPriorityLabelKey(code))
    },
    priorityVariant(priorityValue) {
      const code = mapApiPriorityToCode(priorityValue)
      return getPriorityVariant(code)
    },
    statusLabel(status) {
      if (!status) {
        return '-'
      }

      const i18nKey = `partner.order-request.statuses.${status}`
      if (typeof this.$te === 'function' && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }

      return status
    },
    statusVariant(status) {
      return getExternalOrderRequestStatusVariant(status)
    },
    formatDate(value) {
      if (!value) {
        return '-'
      }

      return transformDatetimeToHumanReadablePretty(value)
    },
  },
}
</script>
